import { AnalyticsEvent, ToolUsageEvent, NavigationEvent, ErrorEvent } from '../types/analytics'
import { sessionManager } from './SessionManager'
import { eventQueue } from './EventQueue'
import { userIdentityService } from './UserIdentityService'
import { navigationTracker } from './NavigationTracker'

/**
 * AnalyticsService - Main interface for tracking user analytics
 * 
 * Provides high-level methods for tracking different types of events,
 * coordinates with SessionManager and EventQueue for reliable data capture.
 */
export class AnalyticsService {
  private initialized: boolean = false

  /**
   * Initialize analytics system
   */
  async initialize(): Promise<void> {
    if (this.initialized) return

    try {
      // Start session
      await sessionManager.startSession()
      this.initialized = true
      
      console.log('Analytics system initialized')
    } catch (error) {
      console.error('Failed to initialize analytics:', error)
    }
  }

  /**
   * Track tool usage events
   */
  async trackToolStart(toolName: string, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('tool_usage', 'tool_start', {
      toolName,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession() // Keep session alive
  }

  async trackToolComplete(toolName: string, duration: number, success: boolean, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('tool_usage', 'tool_complete', {
      toolName,
      success,
      duration,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track file operations
   */
  async trackFileUpload(fileType: string, fileSize: number, success: boolean, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('file_operation', 'file_upload', {
      fileType,
      fileSize,
      success,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  async trackFileDownload(fileType: string, source: string, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('file_operation', 'file_download', {
      fileType,
      source,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track analysis operations
   */
  async trackAnalysisStart(analysisType: string, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('analysis', 'analysis_start', {
      analysisType,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  async trackAnalysisComplete(analysisType: string, duration: number, success: boolean, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('analysis', 'analysis_complete', {
      analysisType,
      duration,
      success,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track navigation events
   */
  async trackNavigation(fromPath: string, toPath: string, method: string = 'click'): Promise<void> {
    await this.ensureInitialized()
    
    // Use NavigationTracker to prevent duplicates
    if (!navigationTracker.shouldTrackNavigation(fromPath, toPath, method, 'trackNavigation')) {
      return
    }
    
    const event = this.createEvent('navigation', 'page_change', {
      fromPath,
      toPath,
      method
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track error events
   */
  async trackError(errorType: string, errorMessage: string, component?: string, stackTrace?: string): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('error', 'error_occurred', {
      errorType,
      errorMessage,
      component,
      stackTrace
    })
    
    eventQueue.enqueue(event)
    // Note: Don't extend session for errors - user might be inactive
  }

  /**
   * Track tool loaded events
   */
  async trackToolLoaded(toolName: string, stepName?: string, stepNumber?: number, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    // Use NavigationTracker to prevent duplicates from StrictMode
    if (!navigationTracker.shouldTrackToolLoading(toolName, stepName, stepNumber)) {
      return
    }
    
    const event = this.createEvent('tool_loaded', 'tool_initialized', {
      toolName,
      stepName,
      stepNumber,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track step progression events
   */
  async trackStepProgression(toolName: string, fromStep: string, toStep: string, fromStepNumber: number, toStepNumber: number, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('step_progression', 'step_changed', {
      toolName,
      stepName: toStep,
      stepNumber: toStepNumber,
      fromStep,
      toStep,
      fromStepNumber,
      toStepNumber,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track page loaded events
   */
  async trackPageLoaded(pageName: string, toolName?: string, stepName?: string, stepNumber?: number, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('page_loaded', 'page_initialized', {
      pageName,
      toolName,
      stepName,
      stepNumber,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track feature usage events
   */
  async trackFeatureUsage(featureName: string, action: string, toolName?: string, stepName?: string, stepNumber?: number, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('feature_usage', action, {
      featureName,
      toolName,
      stepName,
      stepNumber,
      ...properties
    })
    
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track session events
   */
  async trackSessionEvent(action: string, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent('session', action, properties)
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Track custom events
   */
  async trackCustomEvent(eventType: string, action: string, properties?: Record<string, any>): Promise<void> {
    await this.ensureInitialized()
    
    const event = this.createEvent(eventType, action, properties)
    eventQueue.enqueue(event)
    sessionManager.extendSession()
  }

  /**
   * Get current session info
   */
  getCurrentSession() {
    return sessionManager.getCurrentSession()
  }

  /**
   * Get queue status
   */
  getQueueStatus() {
    return {
      queueSize: eventQueue.size(),
      isInitialized: this.initialized
    }
  }

  /**
   * Manually flush pending events
   */
  async flush(): Promise<void> {
    await eventQueue.flush()
  }

  /**
   * End current session (usually called on app close)
   */
  async endSession(): Promise<void> {
    await sessionManager.endSession()
  }

  /**
   * Create a standardized analytics event
   */
  private createEvent(eventType: string, action: string, properties?: Record<string, any>): AnalyticsEvent {
    const session = sessionManager.getCurrentSession()
    
    if (!session) {
      throw new Error('No active session - analytics not properly initialized')
    }

    const userIdentity = userIdentityService.getIdentity()
    if (!userIdentity) {
      throw new Error('No user identity found - user registration required')
    }

    // Extract tool context fields from properties
    const toolName = properties?.toolName
    const pageName = properties?.pageName
    const stepName = properties?.stepName
    const stepNumber = properties?.stepNumber
    const duration = properties?.duration

    // Create clean properties object without extracted fields
    const cleanProperties = { ...properties }
    delete cleanProperties.toolName
    delete cleanProperties.pageName
    delete cleanProperties.stepName
    delete cleanProperties.stepNumber
    delete cleanProperties.duration

    return {
      eventId: crypto.randomUUID(),
      userId: session.userId,
      userEmail: userIdentity.email,
      userDepartment: userIdentity.department,
      sessionId: session.sessionId,
      eventType,
      action,
      timestamp: Date.now(),
      toolName,
      pageName,
      stepName,
      stepNumber,
      properties: cleanProperties,
      duration
    }
  }

  /**
   * Ensure analytics is initialized
   */
  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.initialize()
    }
  }
}

// Create singleton instance
export const analyticsService = new AnalyticsService()